import type { TowerKit } from "../kit/types";

/**
 * Winterhold (Season 3, "The Frozen Front": the War Campaign's Premium tier 30; docs/SHOP.md §15) — every tower a
 * northern ice-hold (contract tools/art/skins/README.md; big kit, §11).
 *
 * The stock roof goes and a steep fir-shingle roof takes its place, pine and dark shingle in courses under a snow cap on
 * its peak, a weathered timber fascia with a deep-red rail, snow caught along the eave in drifts, a fringe of icicles
 * under it that glints with every shot, a great pair of antlers on the peak (on the archer, poison and beacon the roof
 * stands on four fir posts so the bow, the brew and the orb stay in view). Over the top an aurora drifts: a soft curtain
 * of northern light, pale green into teal into rose, fading at its top edge and thinning away at its ends, turning
 * slowly with light running along it and its hem rippling. A shield-wall of round shields (weathered planks and deep red
 * in turn, iron rims, steel bosses) rings each drum's top with snow drifted on its rail, a low bank of frozen earth and
 * rock with snow lying on its top rings the plinth, frost-stiff red banners stand on poles in the snow at the side
 * corners and a snowy fir at the front. The walls are weathered dark stone, the timber dark and grey: a fort in the snow,
 * not a snow cake. Lightning keeps its roof (its coil spire stands in it), now snowy, and wears a ring of antlers round
 * its gallery; frost wears it round its crystal, the arcane spire on its collar, the hex totem on its pole. Weathered
 * stone and timber, pine and deep red lead, snow on the ledges; ice is only an accent (never an icy-blue slot: the
 * frost tower's element hue). In a
 * level: snowflakes drift down off the roof with a few glints, each shot winds up in pale sparks and its hits alternate
 * between a burst of ice shards and a snow splash. Props: art/hq/skins/winterhold/kit.glb (tools/art/skins/winterhold.py).
 */
/** The roof storeys that hold the weapon or the element (bow, brew, the beacon's orb): the roof stands on posts. */
const PAVILIONS = ["archer", "poison", "beacon"];
/** The closed roofs (the back roofs of the cannon, fire, ballista and mortar; the barracks hall). */
const CLOSED = ["cannon", "fire", "ballista", "mortar", "barracks"];
/** The beacon's orb stands in its roof's upper half: its post rises this far, metres (as Bronze's pavilion). */
const BEACON_RISE = 0.27;
const ROOF_MARGIN = 0.06, FIRE_MARGIN = 0.35;
/** The snowbank sits on the plinth's top and spills over its edge (metres above the envelope's foot). */
const PLINTH_TOP = 0.03, BARRACKS_PLINTH = 0.4;
/** The aurora: how far over the apex it floats, its size and its slow turn (rad/s). */
const AURORA = { lift: 0.4, scale: [1.5, 1.6, 1.7] as const, spin: 0.22 } as const;
/** Snow white, a pale ice-white for the wind-up and the shards (not the frost tower's saturated ice blue). */
const SNOW = 0xf4f7fa, PALE = 0xe8eef2;

export const WINTERHOLD: TowerKit = {
  id: "tower.winterhold",
  asset: "skins/winterhold/kit",
  hide: ["roof"],
  // the hex totem keeps its feather crown (its mask stands in it) and the arcane spire its collar (the crystal floats
  // on it): both wear the antler ring on their sockets instead
  hideFit: { hex: [], arcane: [] },
  tier4: true,
  slots: {
    // weathered northern stone, dark and streaked (the snow is on the ledges, not the walls)
    stone: { tint: 0x7a766d, mix: 0.85, gain: 0.85, roughness: 0.95, metalness: 0, keepAccents: false, pattern: { kind: "veins", colour: 0x3a3833, scale: 3.5, strength: 0.65 } },
    roof: { tint: 0xdde3e8, mix: 0.95, gain: 1.0, roughness: 0.85, metalness: 0, keepAccents: false, pattern: { kind: "flecks", colour: 0xffffff, scale: 40, strength: 0.3, glow: 0.6 } },
    wood: { tint: 0x33261b, mix: 0.9, gain: 0.8, roughness: 0.85, keepAccents: false, pattern: { kind: "grain", colour: 0x150e08, scale: 10, strength: 0.55 } },
    metal: { tint: 0x8d9399, mix: 0.9, metalness: 0.85, roughness: 0.35, keepAccents: false, trim: { colour: 0xdde4ea, strength: 0.3 } },
    cloth: { tint: 0x7a1a1f, mix: 0.95, gain: 1.05, roughness: 0.75, keepAccents: false, trim: { colour: 0xc9cfd4, strength: 0.35 } },
  },
  attachments: [
    // the snow roof in the hidden roof's place, its icicles flaring as each shot nears; the barracks' hall takes it
    // boxed to its own ridge
    { piece: "snowroof", at: [], towers: CLOSED, ifHidden: "roof", fitTo: { to: "roof", height: "keep", margin: ROOF_MARGIN }, motion: { charge: { glow: 2.5 } }, fit: {
      barracks: { fitTo: { to: "roof", shape: "box", margin: 0.04 } },
      // the fire tower's chimney (its roof) is tall and slim: the top takes its full height, a little wider
      fire: { fitTo: { to: "roof", margin: FIRE_MARGIN } },
    } },
    { piece: "snowpost", at: [], towers: PAVILIONS, ifHidden: "roof", fitTo: { to: "roof", margin: ROOF_MARGIN }, motion: { charge: { glow: 2.5 } }, fit: { beacon: { offset: [0, BEACON_RISE, 0] } } },
    // the aurora drifting over the top: turning slowly, light running along it, its hem rippling
    { piece: "aurora", at: ["apex"], offset: [0, AURORA.lift, 0], scale: AURORA.scale,
      motion: { spin: AURORA.spin, flow: { speed: 0.1, length: 0.45, along: "right", glow: 0.9 }, wave: 0.014, waveK: 8, hz: 0.3 },
      fit: { frost: { offset: [0, 0.25, 0] }, arcane: { offset: [0, 0.55, 0], scale: [1.1, 1.2, 1.3] }, hex: { offset: [0, 0.45, 0], scale: [1.2, 1.3, 1.4] } } },
    // the shield-wall round each drum's top under a ridge of snow
    { piece: "shieldring", at: [], fitTo: { to: "drum", taper: true, margin: 0.05 } },
    { piece: "drift", at: [], fitTo: { to: "plinth", height: "keep" }, offset: [0, PLINTH_TOP, 0], fit: { barracks: { offset: [0, BARRACKS_PLINTH, 0] } } },
    // the antler ring where the top stays: frost's battlement, lightning's gallery, the arcane collar, the hex pole
    { piece: "antlerring", at: [], towers: ["frost", "lightning", "arcane", "hex"], fitTo: { to: "gallery", height: "keep", margin: 0.08 }, fit: {
      lightning: { scale: 0.85 },
      arcane: { fitTo: null, at: ["apex"], scale: [0.45, 0.48, 0.5] },
      hex: { fitTo: null, at: ["top"], scale: [0.36, 0.38, 0.4] },
    } },
    // frost-stiff banners on poles planted in the snow at the side corners (they barely stir)
    { piece: "bannerpole", at: ["corner0", "corner2"], scale: [1.35, 1.45, 1.55], motion: { sway: 1.2, hz: 0.25 } },
    { piece: "firtree", at: ["corner3"], scale: [1.7, 1.85, 2.0] },
    // warm iron lanterns swinging under the shield-wall, on the two faces the camera sees by turns
    { piece: "lantern", at: ["wall3", "wall1"], offset: [0, 0.12, 0.01], scale: [1.7, 1.85, 2.0], fit: {
      arcane: { scale: [1.2, 1.3, 1.4] }, hex: { scale: [1.2, 1.3, 1.4] },
    } },
    { piece: "glint", at: ["apex"], offset: [0.08, 0.05, 0.08], scale: 1.4, detail: true, motion: { twinkle: 0.33, hz: 0.4 } },
    { piece: "glint", at: ["corner3"], offset: [0, 0.44, 0.0], scale: 1.0, detail: true, motion: { twinkle: 0.29, hz: 0.4 } },
  ],
  // snowflakes drifting down off the roof, a few glints among them
  idle: {
    kind: "leaves", colour: SNOW, every: [1.5, 2.5], at: "apex", lift: -0.1, count: 4, radius: 0.3,
    also: { kind: "glints", colour: PALE, count: 2, radius: 0.2 },
    fit: { frost: { at: "top", lift: 0.0 }, lightning: { at: "band0", lift: 0.2 }, arcane: { at: "band0", lift: 0.1 }, hex: { at: "band0", lift: 0.1 } },
  },
  // ice shards, then a snow splash; each shot winds up in pale sparks
  flourish: {
    impact: { kind: "shatter", colour: PALE, count: 7 },
    impacts: [{ kind: "shatter", colour: PALE, count: 7 }, { kind: "splash", colour: SNOW, count: 7 }],
    muzzle: { colour: SNOW, count: 3 },
    charge: { colour: PALE, count: 4 },
  },
  preview: { tower: "archer", tier: 3 },
};
